import { useCallback, useEffect, useState } from 'react';

import { userAPI } from '../UserAPI';
import { isOwnRole, parseRoles } from '../roleUtils';

/** A role the customer created in the team management ("Eigene Rollen"). `id` is the role type the server matches on. */
export interface OwnRole {
    id: string;
    name: string;
}

/**
 * The customer's own roles, loaded once while `enabled` and again on `reload()`.
 *
 * Only these and the three system roles can be addressed by an alert: the server matches a role by its
 * type, and the other two groups of the team management (Legacy roles of another tenant) are not
 * maintained. A failed load is not an error here — the list just stays as it was, so the system roles
 * remain choosable.
 */
export const useOwnRoles = (customer: string | undefined, enabled: boolean): { roles: OwnRole[]; reload: () => void } => {
    const [roles, setRoles] = useState<OwnRole[]>([]);
    const [round, setRound] = useState(0);
    const reload = useCallback(() => setRound((current) => current + 1), []);

    useEffect(() => {
        if (!enabled || !customer) {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const parsed = parseRoles(await userAPI.getRoles(customer));
                const own = parsed
                    .filter((role) => isOwnRole(role, customer))
                    .map((role) => ({ id: role.getRoleID(), name: role.getRoleName() }))
                    .filter((role) => role.id && role.name);
                const unique = Array.from(new Map(own.map((role) => [role.id, role])).values());

                if (!cancelled) setRoles(unique.sort((a, b) => a.name.localeCompare(b.name, 'de')));
            } catch {
                // Keep the previous list.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [customer, enabled, round]);

    return { roles, reload };
};
